import {
  Box,
  Button,
  Flex,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { getChainNameFromChainId } from "configs/chain";
import { useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import { generateTokenLink, subAddress } from "utils/helpers";
import ModalAddOrUpdateTokenInfo from "./ModalAddOrUpdate";
import ModalRemoveTokenInfo from "./ModalRemove";

const columns = [
  {
    Header: "Address",
    accessor: "address",
  },
  {
    Header: "Chart",
    accessor: "chart",
  },
  {
    Header: "ChainId",
    accessor: "chainId",
  },
  {
    Header: "Owner",
    accessor: "owner",
  },
  {
    Header: "Website",
    accessor: "website",
  },
  {
    Header: "Telegram",
    accessor: "telegram",
  },
  {
    Header: "Twitter",
    accessor: "twitter",
  },
  {
    Header: "Discord",
    accessor: "discord",
  },
  {
    Header: "Github",
    accessor: "github",
  },
  {
    Header: "Description",
    accessor: "description",
  },
  {
    Header: "Validators",
    accessor: "validators",
  },
  {
    Header: "UpdatedBy",
    accessor: "updatedBy",
  },
  {
    Header: "Action",
    accessor: "_id",
  },
];

interface IProps {
  tableData: any;
}
export default function TokenInfoTable({ tableData }: IProps) {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const toast = useToast();

  const {
    isOpen: isOpenRemove,
    onOpen: onOpenRemove,
    onClose: onCloseRemove,
  } = useDisclosure();
  const {
    isOpen: isOpenUpdate,
    onOpen: onOpenUpdate,
    onClose: onCloseUpdate,
  } = useDisclosure();

  const [rowObj, setRowObj] = useState(null);
  const tableInstance = useTable(
    {
      columns,
      data: tableData || [],
      initialState: {
        pageSize: 10,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;

  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const bgColor = useColorModeValue("white", "navy.800");

  const handleOpenRemove = (row: any) => {
    setRowObj(row);
    onOpenRemove();
  };

  const handleUpdate = (row: any) => {
    setRowObj(row);
    onOpenUpdate();
  };

  const handleCopy = (address: string | undefined) => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    toast({
      title: "Copy successfully",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  return (
    <>
      <ModalRemoveTokenInfo
        isOpen={isOpenRemove}
        onClose={onCloseRemove}
        currentId={rowObj?._id}
      />
      <ModalAddOrUpdateTokenInfo
        isOpen={isOpenUpdate}
        onClose={onCloseUpdate}
        currentRow={rowObj}
      />

      <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                  bgColor={bgColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: "10px", lg: "12px" }}
                    color="gray.400"
                  >
                    {column.render("Header")}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody
          {...getTableBodyProps()}
          color={textColor}
          fontWeight={"bold"}
          fontSize={"sm"}
        >
          {page?.map((row: any, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                <Td
                  onClick={() => handleCopy(row?.original?.address)}
                  cursor="copy"
                >
                  {subAddress(row?.original?.address)}
                </Td>
                <Td>
                  <Link
                    color="blue"
                    href={generateTokenLink(
                      row?.original?.chainId,
                      row?.original?.address
                    )}
                    isExternal={true}
                  >
                    Chart
                  </Link>
                </Td>
                <Td>
                  {getChainNameFromChainId(Number(row?.original?.chainId))}
                </Td>
                <Td
                  onClick={() => handleCopy(row?.original?.owner)}
                  cursor="copy"
                >
                  {subAddress(row?.original?.owner)}
                </Td>
                <Td>
                  {row?.original?.website ? (
                    <Link href={row?.original?.website} target={"_blank"}>
                      {row?.original?.website}
                    </Link>
                  ) : null}
                </Td>
                <Td>
                  {row?.original?.telegram ? (
                    <Link href={row?.original?.telegram} target={"_blank"}>
                      {row?.original?.telegram}
                    </Link>
                  ) : null}
                </Td>

                <Td>
                  {row?.original?.twitter ? (
                    <Link href={row?.original?.twitter} target={"_blank"}>
                      {row?.original?.twitter}
                    </Link>
                  ) : null}
                </Td>

                <Td>
                  {row?.original?.discord ? (
                    <Link href={row?.original?.discord} target={"_blank"}>
                      {row?.original?.discord}
                    </Link>
                  ) : null}
                </Td>

                <Td>
                  {row?.original?.github ? (
                    <Link href={row?.original?.github} target={"_blank"}>
                      {row?.original?.github}
                    </Link>
                  ) : null}
                </Td>

                <Td>
                  <Tooltip
                    label={row?.original?.description}
                    aria-label="A tooltip"
                  >
                    <Text noOfLines={2}>{row?.original?.description}</Text>
                  </Tooltip>
                </Td>
                <Td>
                  {row?.original?.validators?.map(
                    (el: { type: string; url: string }) => (
                      <Text>
                        {el.type.toUpperCase()}&nbsp;&nbsp;{el.url}
                      </Text>
                    )
                  )}
                </Td>

                <Td>
                  {row?.original?.updatedBy ? row?.original?.updatedBy : null}
                </Td>

                <Td display={"flex"} alignContent="center">
                  <Button
                    colorScheme="blue"
                    mr="5px"
                    mt="5px"
                    size="sm"
                    onClick={() => handleUpdate(row?.original)}
                  >
                    Update
                  </Button>
                  {/* <Button
                    colorScheme='red'
                    mr='5px'
                    mt='5px'
                    size='sm'
                    onClick={() => handleOpenRemove(row?.original)}
                  >
                    Delete
                  </Button> */}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </>
  );
}
